import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useState, useEffect } from 'react';
import IconSvg from 'src/assets/icons/IconSvg';
import Button from '../button/Button';
import { useViewport } from 'src/context/ViewportContext';
import type { RaffleFilterState, RaffleTypeFilter, EndDateQuickFilter } from 'src/types/raffleFilters';

interface Props {
  btnClass?: string;
  filterState?: RaffleFilterState;
  onApplyFilters?: (filters: RaffleFilterState) => void;
}

const RaffleFilterMenu = ({ btnClass, filterState, onApplyFilters }: Props) => {
  const [activeRaffleType, setActiveRaffleType] = useState<RaffleTypeFilter>(
    filterState?.raffleType ?? 'all',
  );
  const [endDate, setEndDate] = useState<EndDateQuickFilter>(
    filterState?.endDateWithin ?? '',
  );
  const [priceMin, setPriceMin] = useState(
    filterState?.ticketPriceMin != null ? String(filterState.ticketPriceMin) : '',
  );
  const [priceMax, setPriceMax] = useState(
    filterState?.ticketPriceMax != null ? String(filterState.ticketPriceMax) : '',
  );
  const { isMobile } = useViewport();

  useEffect(() => {
    if (filterState) {
      setActiveRaffleType(filterState.raffleType);
      setEndDate(filterState.endDateWithin);
      setPriceMin(filterState.ticketPriceMin != null ? String(filterState.ticketPriceMin) : '');
      setPriceMax(filterState.ticketPriceMax != null ? String(filterState.ticketPriceMax) : '');
    }
  }, [filterState]);

  const raffleTypes: { key: RaffleTypeFilter; label: string }[] = [
    { key: 'live', label: 'Live Raffles' },
    { key: 'past', label: 'Past Raffles' },
  ];

  const endDates: EndDateQuickFilter[] = ['1H', '6H', '24H', '7D', '14D'];

  const handleApply = () => {
    onApplyFilters?.({
      raffleType: activeRaffleType,
      ticketPriceMin: priceMin ? Number(priceMin) : null,
      ticketPriceMax: priceMax ? Number(priceMax) : null,
      endDateWithin: endDate,
    });
  };

  return (
    <Menu>
      <MenuButton
        className={`xl:p-2.5 w-[34px] h-[34px] xl:w-fit xl:h-fit rounded-lg bg-grayscale-900 border border-grayscale-700 text-grayscale-300 flex items-center justify-center flex-shrink-0 ${btnClass}`}
      >
        <IconSvg icon={isMobile ? 'filtersS' : 'filtersM'} />
      </MenuButton>

      <MenuItems
        transition
        anchor="bottom end"
        className="z-50 px-4 py-3 rounded-lg bg-grayscale-900 w-[280px] space-y-3 !mt-1.5"
      >
        <div className="flex text-lg font-medium rounded-md bg-grayscale-800 text-grayscale-500">
          {raffleTypes.map((tab) => (
            <button
              key={tab.key}
              className={`px-4 h-[42px] flex items-center border border-transparent transition-all duration-200 text-base ${
                activeRaffleType === tab.key
                  ? '!border-brand-tangerine bg-brand-black text-brand-magnolia rounded-md'
                  : ''
              }`}
              onClick={() => setActiveRaffleType(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="space-y-1">
          <div className="text-grayscale-400">Ticket Price (sats)</div>
          <div className="flex gap-2">
            <div className="relative">
              <IconSvg icon="bitcoinS" className="absolute left-3 top-[12px] text-grayscale-400" />
              <input
                type="text"
                className="px-3 py-2 pl-[29px] w-full rounded-lg bg-brand-black text-brand-magnolia placeholder:text-brand-granite outline-none"
                placeholder="Min"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            <div className="relative">
              <IconSvg icon="bitcoinS" className="absolute left-3 top-[12px] text-grayscale-400" />
              <input
                type="text"
                className="px-3 py-2 pl-[29px] w-full rounded-lg bg-brand-black text-brand-magnolia placeholder:text-brand-granite outline-none"
                placeholder="Max"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-grayscale-400">End Date</div>
          <div className="flex justify-between">
            {endDates.map((item) => (
              <button
                key={item}
                className={`w-[44px] p-2 rounded-lg bg-brand-black text-brand-granite text-sm text-center ${
                  endDate === item ? '!bg-grayscale-700 text-brand-magnolia' : ''
                }`}
                onClick={() => setEndDate(endDate === item ? '' : item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <MenuItem as="div">
          <Button variant="secondary" size="sm" className="w-full" onClick={handleApply}>
            Apply Filters
          </Button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
};

export default RaffleFilterMenu;
